import { Directive, Input, OnInit, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { PermissionService } from '../services/permission.service';

@Directive({
  selector: '[hasPermission]',
  standalone: true
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private currentPermissions: string[] = [];
  private currentRoles: string[] = [];
  private requireAll: boolean = false;
  private requireAnyRole: boolean = false;

  @Input() set hasPermission(permissions: string | string[]) {
    this.currentPermissions = Array.isArray(permissions) ? permissions : [permissions];
    this.updateView();
  }

  @Input() set hasPermissionRequireAll(value: boolean) {
    this.requireAll = value;
    this.updateView();
  }

  @Input() set hasRole(roles: string | string[]) {
    this.currentRoles = Array.isArray(roles) ? roles : [roles];
    this.updateView();
  }

  @Input() set hasRoleRequireAny(value: boolean) {
    this.requireAnyRole = value;
    this.updateView();
  }

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private permissionService: PermissionService
  ) {}

  ngOnInit() {
    // Subscribe to permission changes
    this.permissionService.userPermissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView() {
    // Always clear the view container first
    this.viewContainer.clear();
    
    if (this.checkPermissions()) {
      // Show the element
      this.viewContainer.createEmbeddedView(this.templateRef);
    }
  }

  private checkPermissions(): boolean {
    // SUPER_DEV_ADMIN bypasses all permission checks
    const userPermissions = this.permissionService.getUserPermissions();
    if (userPermissions?.role?.identification === 'SUPER_DEV_ADMIN') {
      return true;
    }

    // If no permissions or roles specified, show the element
    if (this.currentPermissions.length === 0 && this.currentRoles.length === 0) {
      return true;
    }

    let hasPermission = true;
    let hasRole = true;

    // Check permissions
    if (this.currentPermissions.length > 0) {
      if (this.requireAll) {
        hasPermission = this.permissionService.hasAllPermissions(this.currentPermissions);
      } else {
        hasPermission = this.permissionService.hasAnyPermission(this.currentPermissions);
      }
    }

    // Check roles
    if (this.currentRoles.length > 0) {
      if (this.requireAnyRole) {
        hasRole = this.permissionService.hasAnyRole(this.currentRoles);
      } else {
        // Default behavior: any role matches
        hasRole = this.permissionService.hasAnyRole(this.currentRoles);
      }
    }

    // Both permission and role checks must pass
    return hasPermission && hasRole;
  }
}
